import {
  Await,
  createFileRoute,
  type ErrorComponentProps,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { CircularProgress, Box, Typography } from "@mui/material";
import { GenericTable } from "../../components/GenericTable";
import { z } from "zod";
import { ensureAuthenticated } from "../../utils/auth";
import { Route as ProductRoute } from "../products/route";
import { FilterSidebarWrapper } from "../../components/filterSidebar/FilterSidebarWrapper";
import FilterSidebarButton from "../../components/filterSidebar/FilterSidebarButton.tsx";
import { FilterSidebarLayout } from "../../components/filterSidebar/FilterSidebarLayout";
import {
  loadProductsData,
  type MappedProductResult,
} from "../../utils/product-loader";
import { routerHead } from "../../utils/head";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProductFilters,
  ProductFilterState,
} from "../../components/filterSidebar/ProductFilters.tsx";
import { getProductColumns } from "../../components/Product/productColumns.tsx";
import { useSetAtom } from "jotai";
import { filterSidebarAtom } from "../../atoms/filterSidebarAtom";
import { DetailDrawer } from "../../components/DetailDrawer";
import { getProductDetailTabs } from "../../components/ProductDetailTabs/getProductDetailTabs";
import { useProductDetails } from "../../hooks/useProductDetails";

export interface ProductRow {
  productCode: string;
  productName: string;
  productGroupCode: string;
  ingangsdatum: string;
  gewascode: string;
  hasprimarycharacteristic: boolean;
  vervallen: boolean;
  vbnChangeDateTime?: string;
}

const searchSchema = z.object({
  page: z.string().optional(),
  size: z.string().optional(),
  productCode: z.string().optional(),
  productName: z.string().optional(),
  productGroupCode: z.string().optional(),
  ingangsdatum: z.string().optional(),
  gewasnummer: z.string().optional(),
  showExpired: z.string().optional(),
  showPrimaryCharacteristic: z.string().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/products")({
  beforeLoad: ensureAuthenticated,
  loaderDeps: ({ search }: { search: SearchParams }) => ({
    page: search.page,
    size: search.size,
    productCode: search.productCode,
    productName: search.productName,
    productGroupCode: search.productGroupCode,
    ingangsdatum: search.ingangsdatum,
    gewasnummer: search.gewasnummer,
    showExpired: search.showExpired,
    showPrimaryCharacteristic: search.showPrimaryCharacteristic,
  }),
  loader: async ({ location }: { location: { search: SearchParams } }) =>
    loadProductsData(location.search),
  gcTime: 0,
  validateSearch: searchSchema,
  component: RouteComponent,
  head: routerHead("Producten - Productadministratie"),
  pendingComponent: () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
      }}
    >
      <CircularProgress />
    </div>
  ),
  errorComponent: ({ error }: ErrorComponentProps) => {
    console.error("Error loading products:", error);
    return (
      <div style={{ padding: "2rem" }}>
        <h2>Unable to load products</h2>
        <p>
          The backend API endpoint might not be implemented yet. Please check
          that the server is running and the endpoint exists.
        </p>
        {import.meta.env.DEV && (
          <details style={{ marginTop: "1rem" }}>
            <summary>Error details (Development only)</summary>
            <pre
              style={{
                background: "#f5f5f5",
                padding: "1rem",
                borderRadius: "4px",
                overflow: "auto",
              }}
            >
              {error instanceof Error ? error.message : String(error)}
            </pre>
          </details>
        )}
      </div>
    );
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: ProductRoute.id });
  const { deferredData } = Route.useLoaderData();
  const setFilterOpen = useSetAtom(filterSidebarAtom);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // Prefetch product details when a row is selected
  const _productQuery = useProductDetails(selectedProductId ?? "");

  // Ensure filters are open on product screen startup
  useEffect(() => {
    setFilterOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedProductId(null);
  }, [
    search.productCode,
    search.productName,
    search.productGroupCode,
    search.ingangsdatum,
    search.showExpired,
    search.showPrimaryCharacteristic,
  ]);
  const productColumns = useMemo(() => getProductColumns(t), [t]);

  const filters: ProductFilterState = {
    productCode: search.productCode,
    productName: search.productName,
    productGroupCode: search.productGroupCode,
    ingangsdatum: search.ingangsdatum,
    gewasnummer: (search as any).gewasnummer,
    showExpired: search.showExpired === "true",
    showPrimaryCharacteristic: search.showPrimaryCharacteristic === "true",
  };

  const filterCount = Object.entries(filters).filter(([key, value]) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "boolean") return value === true;
    return false;
  }).length;

  function updateFilters(newFilters: Partial<ProductFilterState>) {
    const convertedFilters: Record<string, any> = { ...newFilters };

    // Convert boolean showExpired to string
    if (typeof convertedFilters.showExpired === "boolean") {
      convertedFilters.showExpired = convertedFilters.showExpired
        ? "true"
        : undefined;
    }

    // Convert boolean showPrimaryCharacteristic to string
    if (typeof convertedFilters.showPrimaryCharacteristic === "boolean") {
      convertedFilters.showPrimaryCharacteristic =
        convertedFilters.showPrimaryCharacteristic ? "true" : undefined;
    }

    navigate({
      to: ProductRoute.fullPath,
      search: (prev) => ({
        ...prev,
        ...convertedFilters,
        page: "1",
      }),
    });
  }

  function handleClearFilters() {
    updateFilters({
      productCode: undefined,
      productName: undefined,
      productGroupCode: undefined,
      ingangsdatum: undefined,
      gewasnummer: undefined,
      showExpired: undefined,
      showPrimaryCharacteristic: undefined,
    });
  }

  return (
    <>
      <FilterSidebarWrapper
        activeFilters={filterCount}
        onClearFilters={handleClearFilters}
      >
        <ProductFilters filters={filters} onFilterChange={updateFilters} />
      </FilterSidebarWrapper>

      <FilterSidebarLayout>
        {/* Main container for grid + drawer layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 120px)", // Account for navigation and top margin
            overflow: "auto",
            pb: 2,
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Typography 
              variant="h1" 
              gutterBottom 
              sx={{ 
                mb: 0,
                fontSize: "1.75rem",
                fontWeight: 600,
                fontFamily: "var(--font-family-halyard-display)",
              }}
            >
              {t("product.pageHeaderTitle")}
            </Typography>
            <FilterSidebarButton />
          </Box>

          {/* Grid area - always visible and scrollable */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "auto",
              position: "relative",
              "& > .MuiCircularProgress-root": {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              },
            }}
          >
            <Await promise={deferredData} fallback={<CircularProgress />}>
              {({ data }: { data: MappedProductResult }) => {
                const hasNoData = !data.value || data.value.length === 0;

                // Determine the appropriate message based on the scenario
                let emptyMessage: string | undefined;
                if (hasNoData) {
                  if (data.error) {
                    // API error occurred (404, 500, network error, etc.)
                    emptyMessage = data.error.statusCode
                      ? t("product.emptyStates.loadErrorWithStatus", {
                          message: data.error.message,
                          statusCode: data.error.statusCode,
                        })
                      : t("product.emptyStates.loadError", {
                          message: data.error.message,
                        });
                  } else if (data["@odata.count"] === 0 && filterCount > 0) {
                    // 200 OK but no results with active filters
                    emptyMessage = t("product.emptyStates.noMatchingFilters");
                  } else {
                    // 200 OK but no results without filters
                    emptyMessage = t("product.emptyStates.noProducts");
                  }
                }

                return (
                  <GenericTable
                    columns={productColumns}
                    rows={data.value || []}
                    rowCount={data["@odata.count"] || 0}
                    defaultPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    emptyMessage={hasNoData ? emptyMessage : undefined}
                    selectedRowId={selectedProductId ?? undefined}
                    getRowId={(row: ProductRow) => row.productCode}
                    onRowClick={(row: ProductRow) => {
                      setSelectedProductId(row.productCode);
                    }}
                    onPageChange={(page) => {
                      navigate({
                        to: ProductRoute.fullPath,
                        search: { ...search, page: String(page) },
                      });
                    }}
                    onPageSizeChange={(pageSize) => {
                      navigate({
                        to: ProductRoute.fullPath,
                        search: {
                          ...search,
                          size: String(pageSize),
                          page: "1",
                        },
                      });
                    }}
                  />
                );
              }}
            </Await>
          </Box>

          {/* Drawer - inline, below grid, same width as grid */}
          <DetailDrawer
            open={!!selectedProductId}
            onClose={() => setSelectedProductId(null)}
            tabs={
              selectedProductId
                ? getProductDetailTabs({ productCode: selectedProductId })
                : []
            }
          />
        </Box>
      </FilterSidebarLayout>
    </>
  );
}
